import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal, viewChild, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { Toast } from '@/shared/components/toast/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { TipoDebitoService } from '@/core/api/sicom-api/financeiro/arrecadacao/tipo-debito/tipo-debito.service';
import { TipoDebitoManutencaoRequest, TipoDebitoManutencaoResponse, TpdebitoPjResponse } from '@/core/api/sicom-api/financeiro/arrecadacao/tipo-debito/tipo-debito.model';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { CardContent } from '@/shared/components/card-content/card-content';
import { SelecionaContaContabil } from '@/shared/components/seleciona-conta-contabil/seleciona-conta-contabil';
import { ContaContabilService } from '@/core/api/sicom-api/financeiro/arrecadacao/conta-contabil/conta-contabil.service';
import { ContaContabilResponse } from '@/core/api/sicom-api/financeiro/arrecadacao/conta-contabil/conta-contabil.model';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface FaixaPJ {
    faixa: number;
    inicial: number | null;
    final: number | null;
    valor: number | null;
}

interface DescontoCronograma {
    label: string;
    data: Date | null;
    percentual: number | null;
}

type RegraCalculo = 'correcao' | 'prescricao' | 'proporcional' | 'cotaParte' | 'taxaPrimeiraInscricao';

const S = 'S';
const N = 'N';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-anuidade-detalhe',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        FormsModule,
        ButtonModule,
        BadgeModule,
        DialogModule,
        SelectButtonModule,
        TagModule,
        TooltipModule,
        ToggleSwitchModule,
        InputTextModule,
        DatePickerModule,
        InputNumberModule,
        TableModule,
        Toast,
        CanDirective,
        CardContent,
        SelecionaContaContabil
    ],
    providers: [MessageService],
    templateUrl: './anuidade-detalhe.html',
    styleUrl: './anuidade-detalhe.scss'
})
/** Modal de novo/edição de anuidade (valores por categoria/situação e vencimentos). */
export class AnuidadeDetalhe implements OnInit {
    protected readonly roleAnuidadesEditar = PERMISSOES.configuracoes.anuidades.editar;

    private readonly tipoDebitoService = inject(TipoDebitoService);
    private readonly contaContabilService = inject(ContaContabilService);
    private readonly messageService = inject(MessageService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly location = inject(Location);
    private readonly seletorConta = viewChild.required(SelecionaContaContabil);
    private contaSetCallback: ((id: string) => void) | null = null;

    // Plano de contas — usado para exibir "código - descrição" nos campos
    protected readonly contasContabil = signal<ContaContabilResponse[]>([]);

    carregando = signal(false);
    salvando = signal(false);
    modoNovo = signal(false);
    editando = signal(false);
    tipoDebitoIdAtual = signal<string | null>(null);
    sgUf = signal('');
    pjDtDescontoConsultorio = signal<Date | null>(null);

    codigo = signal('');
    descricao = signal('');
    tipoDebito = signal('Anuidade');
    tiposDebito = [
        { label: 'Anuidade', value: 'Anuidade' },
        { label: 'Taxa', value: 'Taxa' },
        { label: 'Multa', value: 'Multa' }
    ];

    regrasCalculo = signal<Record<RegraCalculo, boolean>>({
        correcao: false,
        prescricao: false,
        proporcional: false,
        cotaParte: false,
        taxaPrimeiraInscricao: false
    });

    get regrasConfig(): { label: string; model: RegraCalculo }[] {
        return [
            { label: 'Correção', model: 'correcao' },
            { label: 'Prescrição', model: 'prescricao' },
            { label: 'Proporcional', model: 'proporcional' },
            { label: 'Cota Parte', model: 'cotaParte' },
            { label: 'Taxa 1ª Insc.', model: 'taxaPrimeiraInscricao' }
        ];
    }

    multaPercentual = signal<number | null>(null);
    jurosMensalPercentual = signal<number | null>(null);

    descontos = signal<DescontoCronograma[]>([
        { label: '1º Desconto', data: null, percentual: null },
        { label: '2º Desconto', data: null, percentual: null },
        { label: '3º Desconto', data: null, percentual: null }
    ]);

    pfContaId = signal<string | null>(null);
    pfParcelamento = signal(false);
    pfDataParcelamento = signal<Date | null>(null);
    pfValor = signal<number | null>(null);
    pfDescPrimeiraInscricao = signal<number | null>(null);
    pfVencimento = signal<Date | null>(null);

    pjContaId = signal<string | null>(null);
    pjParcelamento = signal(false);
    pjDataParcelamento = signal<Date | null>(null);
    pjVencimento = signal<Date | null>(null);
    pjDescConsultorio = signal<number | null>(null);
    pjFaixas = signal<FaixaPJ[]>([]);

    ngOnInit(): void {
        this.carregarContasContabil();

        this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            const id = params.get('id');
            if (!id || id === 'novo') {
                this.tipoDebitoIdAtual.set(null);
                this.modoNovo.set(true);
                this.editando.set(true);
                this.limparFormulario();
                return;
            }

            this.tipoDebitoIdAtual.set(id);
            this.modoNovo.set(false);
            this.editando.set(false);
            this.carregarDetalhes(id);
        });
    }

    get camposDesabilitados(): boolean {
        return !this.editando() || this.carregando() || this.salvando();
    }

    editar(): void {
        this.editando.set(true);
    }

    // ── Conta contábil (modal compartilhado) ───────────────────────
    getDescricaoConta(id: number | string | null): string {
        if (id == null) return '';
        const conta = this.contasContabil().find((c) => String(c.id) === String(id));
        return conta ? conta.descricao : '';
    }

    getFormatConta(id: number | string | null): string {
        if (id == null) return 'Não configurado';
        const desc = this.getDescricaoConta(id);
        return desc ? `${id} - ${desc}` : String(id);
    }

    abrirSelecionarConta(sinal: WritableSignal<string | null>): void {
        if (this.camposDesabilitados) return;
        this.contaSetCallback = (id: string) => sinal.set(id);
        this.seletorConta().abrir();
    }

    limparConta(sinal: WritableSignal<string | null>): void {
        if (this.camposDesabilitados) return;
        sinal.set(null);
    }

    onContaSelecionada(conta: ContaContabilResponse): void {
        this.contaSetCallback?.(conta.id);
        this.contaSetCallback = null;
    }

    private carregarContasContabil(): void {
        this.contaContabilService
            .listar()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (contas) => this.contasContabil.set(contas),
                error: () => this.contasContabil.set([])
            });
    }

    /** Botão Voltar do cabeçalho — mesmo comportamento do relatorio-container (certidão cadastral). */
    voltar(): void {
        this.location.back();
    }

    updateRegra(model: RegraCalculo, value: boolean): void {
        if (this.camposDesabilitados) return;

        this.regrasCalculo.update((regras) => ({
            ...regras,
            [model]: value
        }));
    }

    getRegraValue(model: RegraCalculo): boolean {
        return this.regrasCalculo()[model];
    }

    // ── Modal de faixa PJ (edição não é mais inline na tabela) ──────────────
    modalFaixaAberto = signal(false);
    faixaEmEdicaoIndex = signal<number | null>(null);
    faixaFormInicial = signal<number | null>(null);
    faixaFormFinal = signal<number | null>(null);
    faixaFormValor = signal<number | null>(null);

    abrirModalNovaFaixa(): void {
        if (this.camposDesabilitados) return;
        this.faixaEmEdicaoIndex.set(null);
        this.faixaFormInicial.set(null);
        this.faixaFormFinal.set(null);
        this.faixaFormValor.set(null);
        this.modalFaixaAberto.set(true);
    }

    abrirModalEditarFaixa(index: number): void {
        if (this.camposDesabilitados) return;
        const faixa = this.pjFaixas()[index];
        if (!faixa) return;
        this.faixaEmEdicaoIndex.set(index);
        this.faixaFormInicial.set(faixa.inicial);
        this.faixaFormFinal.set(faixa.final);
        this.faixaFormValor.set(faixa.valor);
        this.modalFaixaAberto.set(true);
    }

    confirmarFaixa(): void {
        const inicial = this.faixaFormInicial();
        const fim = this.faixaFormFinal();
        const valor = this.faixaFormValor();

        if (valor == null) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe o valor base da faixa.' });
            return;
        }
        if (inicial != null && fim != null && fim < inicial) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'A faixa final deve ser maior ou igual à inicial.' });
            return;
        }

        const index = this.faixaEmEdicaoIndex();
        if (index === null) {
            this.pjFaixas.update((faixas) => [...faixas, { faixa: faixas.length + 1, inicial, final: fim, valor }]);
        } else {
            this.pjFaixas.update((faixas) => faixas.map((f, i) => (i === index ? { ...f, inicial, final: fim, valor } : f)));
        }
        this.modalFaixaAberto.set(false);
    }

    removerFaixa(index: number): void {
        if (this.camposDesabilitados) return;

        this.pjFaixas.update((faixas) => faixas.filter((_, i) => i !== index).map((faixa, i) => ({ ...faixa, faixa: i + 1 })));
    }

    salvar(): void {
        if (this.camposDesabilitados) return;

        if (!this.codigo() || !this.descricao()) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Código e descrição são obrigatórios.' });
            return;
        }

        this.salvando.set(true);
        this.tipoDebitoService
            .salvar(this.buildRequest())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Configuração de débito salva com sucesso.' });
                    this.aposSalvar();
                },
                error: () => this.salvando.set(false),
                complete: () => this.salvando.set(false)
            });
    }

    cancelar(): void {
        if (this.editando() && !this.modoNovo()) {
            const id = this.tipoDebitoIdAtual();
            if (id) {
                this.editando.set(false);
                this.carregarDetalhes(id);
                return;
            }
        }

        this.voltarParaLista();
    }

    private carregarDetalhes(id: string): void {
        this.carregando.set(true);
        this.tipoDebitoService
            .buscarPorId(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => this.popularFormulario(response),
                error: () => this.carregando.set(false),
                complete: () => this.carregando.set(false)
            });
    }

    private popularFormulario(response: TipoDebitoManutencaoResponse): void {
        this.codigo.set(response.id_tpdebito);
        this.descricao.set(response.ds_debito);
        this.sgUf.set(response.sg_uf ?? response.pf?.sg_uf ?? response.pj_list?.[0]?.sg_uf ?? '');
        this.tipoDebito.set(this.tipoDebitoParaTela(response.in_anuidade));
        this.regrasCalculo.set({
            correcao: response.in_correcao === S,
            prescricao: response.in_prescricao === S,
            proporcional: response.in_proporcional === S,
            cotaParte: response.in_cota_parte === S,
            taxaPrimeiraInscricao: response.in_taxa_inscricao === S
        });
        this.multaPercentual.set(response.pe_multa ?? null);
        this.jurosMensalPercentual.set(response.pe_juros ?? null);
        this.descontos.set([
            { label: '1º Desconto', data: this.parseData(response.dt_desc1), percentual: response.pe_desc1 ?? null },
            { label: '2º Desconto', data: this.parseData(response.dt_desc2), percentual: response.pe_desc2 ?? null },
            { label: '3º Desconto', data: this.parseData(response.dt_desc3), percentual: response.pe_desc3 ?? null }
        ]);

        this.pfContaId.set(response.id_conta_pf ?? null);
        this.pfParcelamento.set(response.in_parcelamento === S);
        this.pfDataParcelamento.set(this.parseData(response.dt_parcelamento_pf));
        this.pfValor.set(response.pf?.vr_debito ?? null);
        this.pfDescPrimeiraInscricao.set(response.pf?.pe_desc_primeira ?? null);
        this.pfVencimento.set(this.parseData(response.pf?.dt_vencimento));

        const primeiraFaixa = response.pj_list?.[0];
        this.pjContaId.set(response.id_conta_pj ?? null);
        this.pjParcelamento.set(response.in_parcelamento === S);
        this.pjDataParcelamento.set(this.parseData(response.dt_parcelamento_pj));
        this.pjVencimento.set(this.parseData(primeiraFaixa?.dt_vencimento));
        this.pjDescConsultorio.set(primeiraFaixa?.pe_desconto_consultorio ?? null);
        this.pjDtDescontoConsultorio.set(this.parseData(primeiraFaixa?.dt_desconto_consultorio));
        this.pjFaixas.set((response.pj_list ?? []).map((faixa) => this.pjResponseParaFaixa(faixa)));
    }

    private pjResponseParaFaixa(faixa: TpdebitoPjResponse): FaixaPJ {
        return {
            faixa: faixa.id_faixa,
            inicial: faixa.vr_i_faixa ?? null,
            final: faixa.vr_f_faixa ?? null,
            valor: faixa.vr_debito ?? null
        };
    }

    private limparFormulario(): void {
        this.codigo.set('');
        this.descricao.set('');
        this.sgUf.set('');
        this.tipoDebito.set('Anuidade');
        this.regrasCalculo.set({
            correcao: false,
            prescricao: false,
            proporcional: false,
            cotaParte: false,
            taxaPrimeiraInscricao: false
        });
        this.multaPercentual.set(null);
        this.jurosMensalPercentual.set(null);
        this.descontos.set([
            { label: '1º Desconto', data: null, percentual: null },
            { label: '2º Desconto', data: null, percentual: null },
            { label: '3º Desconto', data: null, percentual: null }
        ]);
        this.pfContaId.set(null);
        this.pfParcelamento.set(false);
        this.pfDataParcelamento.set(null);
        this.pfValor.set(null);
        this.pfDescPrimeiraInscricao.set(null);
        this.pfVencimento.set(null);
        this.pjContaId.set(null);
        this.pjParcelamento.set(false);
        this.pjDataParcelamento.set(null);
        this.pjVencimento.set(null);
        this.pjDescConsultorio.set(null);
        this.pjDtDescontoConsultorio.set(null);
        this.pjFaixas.set([]);
    }

    private buildRequest(): TipoDebitoManutencaoRequest {
        const descontos = this.descontos();
        const parcelamento = this.pfParcelamento() || this.pjParcelamento();

        return {
            id_tpdebito: this.codigo(),
            ds_debito: this.descricao(),
            sg_uf: this.sgUf() || undefined,
            in_anuidade: this.tipoDebitoParaApi(),
            in_correcao: this.flag(this.regrasCalculo().correcao),
            in_cota_parte: this.flag(this.regrasCalculo().cotaParte),
            in_proporcional: this.flag(this.regrasCalculo().proporcional),
            in_prescricao: this.flag(this.regrasCalculo().prescricao),
            in_parcelamento: this.flag(parcelamento),
            in_taxa_inscricao: this.flag(this.regrasCalculo().taxaPrimeiraInscricao),
            pe_multa: this.multaPercentual() ?? undefined,
            pe_juros: this.jurosMensalPercentual() ?? undefined,
            pe_desc1: descontos[0]?.percentual ?? undefined,
            pe_desc2: descontos[1]?.percentual ?? undefined,
            pe_desc3: descontos[2]?.percentual ?? undefined,
            dt_desc1: this.toIsoDate(descontos[0]?.data),
            dt_desc2: this.toIsoDate(descontos[1]?.data),
            dt_desc3: this.toIsoDate(descontos[2]?.data),
            dt_parcelamento_pf: this.toIsoDate(this.pfDataParcelamento()),
            dt_parcelamento_pj: this.toIsoDate(this.pjDataParcelamento()),
            id_conta_pf: this.pfContaId() ?? undefined,
            id_conta_pj: this.pjContaId() ?? undefined,
            pf: {
                vr_debito: this.pfValor() ?? undefined,
                dt_vencimento: this.toIsoDate(this.pfVencimento()),
                pe_desc_primeira: this.pfDescPrimeiraInscricao() ?? undefined
            },
            pj_list: this.pjFaixas().map((faixa) => ({
                id_faixa: faixa.faixa,
                vr_debito: faixa.valor ?? undefined,
                vr_i_faixa: faixa.inicial ?? undefined,
                vr_f_faixa: faixa.final ?? undefined,
                dt_vencimento: this.toIsoDate(this.pjVencimento()),
                pe_desconto_consultorio: this.pjDescConsultorio() ?? undefined,
                dt_desconto_consultorio: this.toIsoDate(this.pjDtDescontoConsultorio())
            }))
        };
    }

    private tipoDebitoParaTela(value: 'S' | 'N' | 'M'): string {
        if (value === 'M') return 'Multa';
        if (value === 'N') return 'Taxa';
        return 'Anuidade';
    }

    private tipoDebitoParaApi(): string {
        if (this.tipoDebito() === 'Multa') return 'M';
        if (this.tipoDebito() === 'Taxa') return 'N';
        return 'S';
    }

    private flag(value: boolean): string {
        return value ? S : N;
    }

    private parseData(value?: string): Date | null {
        return value ? new Date(value) : null;
    }

    private toIsoDate(value?: Date | null): string | undefined {
        return value ? value.toISOString().split('T')[0] : undefined;
    }

    private voltarParaLista(): void {
        void this.router.navigate(['/configuracoes/arrecadacao/anuidades']);
    }

    private aposSalvar(): void {
        const idSalvo = this.codigo();

        if (this.modoNovo()) {
            this.modoNovo.set(false);
            this.editando.set(false);
            this.tipoDebitoIdAtual.set(idSalvo);
            void this.router.navigate(['/configuracoes/arrecadacao/anuidades', idSalvo]);
            return;
        }

        this.editando.set(false);
        this.tipoDebitoIdAtual.set(idSalvo);
        this.carregarDetalhes(idSalvo);
    }
}
